import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { 
  ConversationState, 
  ConversationStarter,
  ConversationGenerationRequest,
  ConversationGenerationResponse
} from '@/types/conversation';
import { 
  requestNewConversations, 
  RequestNewConversationsOptions 
} from '@/api-frontend/requestNewConversations';
import {
  getCuratedConversations,
  deleteConversationStarter as apiDeleteConversation,
  updateConversationStatus as apiUpdateConversationStatus
} from '@/api-frontend/conversationApi';

const CACHE_DURATION = 30 * 60 * 1000; // 30 minutes

// Extended state with additional UI fields
interface ExtendedConversationState extends ConversationState {
  loading: boolean; // alias for isLoading
  filters: {
    searchQuery: string;
    status: 'all' | 'ready' | 'used';
    category?: string;
    sortBy: 'newest' | 'oldest';
  };
  totalCount: number;
}

const initialState: ExtendedConversationState = {
  conversations: [],
  isLoading: false,
  loading: false,
  error: null,
  lastGenerated: null,
  cacheExpiry: CACHE_DURATION,
  generationHistory: [],
  filters: {
    searchQuery: '',
    status: 'all',
    sortBy: 'newest',
  },
  totalCount: 0,
};

// Async thunk to fetch curated conversations
export const fetchCuratedConversations = createAsyncThunk(
  'conversations/fetchCurated',
  async (sessionId: string, { rejectWithValue }) => {
    try {
      const response = await getCuratedConversations(sessionId);
      return response;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch conversations');
    }
  }
);

// Async thunk to delete a conversation starter
export const deleteConversationStarter = createAsyncThunk(
  'conversations/delete',
  async (conversationId: string, { rejectWithValue }) => {
    try {
      await apiDeleteConversation(conversationId);
      return conversationId;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to delete conversation');
    }
  }
);

// Async thunk to update conversation status
export const updateConversationStatus = createAsyncThunk(
  'conversations/updateStatus',
  async ({ id, isActivated }: { id: string; isActivated: boolean }, { rejectWithValue }) => {
    try {
      const updatedConversation = await apiUpdateConversationStatus(id, isActivated);
      return updatedConversation;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to update conversation status');
    }
  }
);

// Async thunk to generate new conversations
export const generateConversations = createAsyncThunk(
  'conversations/generate',
  async (options: RequestNewConversationsOptions, { rejectWithValue }) => {
    const result = await requestNewConversations(options);
    
    if (!result.success) {
      return rejectWithValue(result.error || 'Failed to generate conversations');
    }
    
    return result.data;
  }
);

// Async thunk to regenerate conversations with current context
export const regenerateConversations = createAsyncThunk(
  'conversations/regenerate',
  async (
    options: RequestNewConversationsOptions & { forceRefresh?: boolean },
    { getState, dispatch }
  ) => {
    const state = getState() as { conversations: ConversationState };
    
    // Check if we need to regenerate based on cache expiry
    if (!options.forceRefresh && state.conversations.lastGenerated) {
      const lastGenerated = new Date(state.conversations.lastGenerated).getTime();
      const now = Date.now();
      
      if ((now - lastGenerated) < state.conversations.cacheExpiry) {
        // Return existing conversations if still fresh
        return {
          conversations: state.conversations.conversations,
          contextUsed: {
            location: false,
            weather: false,
            news: false,
          },
          generatedAt: state.conversations.lastGenerated,
          expiresAt: new Date(lastGenerated + state.conversations.cacheExpiry).toISOString(),
        } as ConversationGenerationResponse;
      }
    }
    
    // Generate new conversations
    const result = await dispatch(generateConversations(options));
    return result.payload;
  }
);

const conversationSlice = createSlice({
  name: 'conversations',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearConversations: (state) => {
      state.conversations = [];
      state.lastGenerated = null;
      state.totalCount = 0;
    },
    updateCacheExpiry: (state, action: PayloadAction<number>) => {
      state.cacheExpiry = action.payload;
    },
    addToHistory: (state, action: PayloadAction<ConversationGenerationRequest>) => {
      state.generationHistory.unshift(action.payload);
      // Keep only last 10 requests
      if (state.generationHistory.length > 10) {
        state.generationHistory = state.generationHistory.slice(0, 10);
      }
    },
    clearHistory: (state) => {
      state.generationHistory = [];
    },
    favoriteConversation: (state, action: PayloadAction<string>) => {
      const conversation = state.conversations.find(c => c.id === action.payload);
      if (conversation) {
        // Add a favorite flag (we'll extend the type if needed)
        (conversation as any).isFavorite = true;
      }
    },
    unfavoriteConversation: (state, action: PayloadAction<string>) => {
      const conversation = state.conversations.find(c => c.id === action.payload);
      if (conversation) {
        (conversation as any).isFavorite = false;
      }
    },
    removeConversation: (state, action: PayloadAction<string>) => {
      state.conversations = state.conversations.filter(c => c.id !== action.payload);
    },
    reorderConversations: (state, action: PayloadAction<ConversationStarter[]>) => {
      state.conversations = action.payload;
    },
    // Filter management
    updateSearchQuery: (state, action: PayloadAction<string>) => {
      state.filters.searchQuery = action.payload;
    },
    updateStatusFilter: (state, action: PayloadAction<'all' | 'ready' | 'used'>) => {
      state.filters.status = action.payload;
    },
    updateCategoryFilter: (state, action: PayloadAction<string | undefined>) => {
      state.filters.category = action.payload;
    },
    updateSortBy: (state, action: PayloadAction<'newest' | 'oldest'>) => {
      state.filters.sortBy = action.payload;
    },
    resetFilters: (state) => {
      state.filters = {
        searchQuery: '',
        status: 'all',
        sortBy: 'newest',
      };
    },
  },
  extraReducers: (builder) => {
    builder
      // fetchCuratedConversations
      .addCase(fetchCuratedConversations.pending, (state) => {
        state.isLoading = true;
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCuratedConversations.fulfilled, (state, action) => {
        state.isLoading = false;
        state.loading = false;
        state.conversations = action.payload.conversations;
        state.totalCount = action.payload.totalCount || action.payload.conversations.length;
        state.error = null;
      })
      .addCase(fetchCuratedConversations.rejected, (state, action) => {
        state.isLoading = false;
        state.loading = false;
        state.error = action.payload as string || 'Failed to fetch conversations';
      })

      // deleteConversationStarter
      .addCase(deleteConversationStarter.pending, (state) => {
        state.error = null;
      })
      .addCase(deleteConversationStarter.fulfilled, (state, action) => {
        state.conversations = state.conversations.filter(c => c.id !== action.payload);
        state.totalCount = Math.max(0, state.totalCount - 1);
      })
      .addCase(deleteConversationStarter.rejected, (state, action) => {
        state.error = action.payload as string || 'Failed to delete conversation';
      })

      // updateConversationStatus
      .addCase(updateConversationStatus.pending, (state) => {
        state.error = null;
      })
      .addCase(updateConversationStatus.fulfilled, (state, action) => {
        const index = state.conversations.findIndex(c => c.id === action.payload.id);
        if (index !== -1) {
          state.conversations[index] = action.payload;
        }
      })
      .addCase(updateConversationStatus.rejected, (state, action) => {
        state.error = action.payload as string || 'Failed to update conversation status';
      })

      // generateConversations
      .addCase(generateConversations.pending, (state) => {
        state.isLoading = true;
        state.loading = true;
        state.error = null;
      })
      .addCase(generateConversations.fulfilled, (state, action) => {
        state.isLoading = false;
        state.loading = false;
        if (action.payload) {
          state.conversations = action.payload.conversations;
          state.totalCount = action.payload.conversations.length;
          state.lastGenerated = action.payload.generatedAt;
          state.error = null;
        }
      })
      .addCase(generateConversations.rejected, (state, action) => {
        state.isLoading = false;
        state.loading = false;
        state.error = action.payload as string || 'Failed to generate conversations';
      })

      // regenerateConversations
      .addCase(regenerateConversations.pending, (state) => {
        state.isLoading = true;
        state.loading = true;
        state.error = null;
      })
      .addCase(regenerateConversations.fulfilled, (state, action) => {
        state.isLoading = false;
        state.loading = false;
        if (action.payload) {
          state.conversations = action.payload.conversations;
          state.totalCount = action.payload.conversations.length;
          state.lastGenerated = action.payload.generatedAt;
          state.error = null;
        }
      })
      .addCase(regenerateConversations.rejected, (state, action) => {
        state.isLoading = false;
        state.loading = false;
        state.error = 'Failed to regenerate conversations';
      });
  },
});

export const {
  clearError,
  clearConversations,
  updateCacheExpiry,
  addToHistory,
  clearHistory,
  favoriteConversation,
  unfavoriteConversation,
  removeConversation,
  reorderConversations,
  updateSearchQuery,
  updateStatusFilter,
  updateCategoryFilter,
  updateSortBy,
  resetFilters,
} = conversationSlice.actions;

export default conversationSlice.reducer;